import { useEffect, useMemo, useState } from 'react';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import { Box, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useConditionActions } from '@retail/calculus/context';
import { SuspenseWithSentryErrorBoundary } from '@retail/components';
import { LevelChip } from '@retail/calculus/components';
import { ContainedButton, Dialog, OutlinedButton, Stepper, TextButton } from '@shared/components';
import { SupplierBase } from '@retail/suppliers/types';
import { ConditionsNew } from '@retail/calculus/types';
import {
  allConditionsDefaultValues,
  AllConditionsFormValues,
  buildConditionsFromForm,
  ConditionStepType,
  editConditionDefaultValues,
  stepValidationSchema,
} from './allConditionsSchema';
import { LevelStep } from './steps/LevelStep';
import { PurchaseStep } from './steps/PurchaseStep';
import { FreightStep } from './steps/FreightStep';
import { SalesPriceStep } from './steps/SalesPriceStep';
import { RoundingStep } from './steps/RoundingStep';
import { SummaryStep } from './steps/SummaryStep';

type StepKey = 'level' | ConditionStepType | 'summary';

type SkipField = 'purchaseSkipped' | 'freightSkipped' | 'salesPriceSkipped' | 'roundingSkipped';

const skipFieldByStep: Record<ConditionStepType, SkipField> = {
  purchase: 'purchaseSkipped',
  freight: 'freightSkipped',
  salesPrice: 'salesPriceSkipped',
  rounding: 'roundingSkipped',
};

interface Props {
  open: boolean;
  selectedSupplier?: SupplierBase;
  editCondition?: ConditionsNew;
  showRounding?: boolean;
  onClose: () => void;
}

export function CreateAllConditionsWizard({
  open,
  selectedSupplier,
  editCondition,
  showRounding = true,
  onClose,
}: Props) {
  const t = useCalculusTFunction();
  const supplierNumber =
    editCondition?.supplier?.supplierNumber ?? selectedSupplier?.supplierNumber;
  const { selectedOrgUnitAssortment } = useSelectedOrgUnit();
  const { createMultipleConditions, isLoadingPostChanges } = useConditionActions();

  const formMethods = useForm<AllConditionsFormValues>({
    defaultValues: editCondition
      ? editConditionDefaultValues(t, editCondition)
      : allConditionsDefaultValues(t, !!supplierNumber),
  });
  const { getValues, setValue, setError, clearErrors, watch } = formMethods;

  useEffect(() => {
    const subscription = watch((_values, { name }) => {
      if (name) clearErrors(name as keyof AllConditionsFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const conditionSteps = useMemo<ConditionStepType[]>(() => {
    const steps: ConditionStepType[] = supplierNumber
      ? ['purchase', 'freight', 'salesPrice', 'rounding']
      : ['salesPrice', 'rounding'];
    return showRounding ? steps : steps.filter((step) => step !== 'rounding');
  }, [supplierNumber, showRounding]);

  // In edit mode the level is locked to the row, so the level step is skipped.
  const steps = useMemo<StepKey[]>(
    () =>
      editCondition ? [...conditionSteps, 'summary'] : ['level', ...conditionSteps, 'summary'],
    [conditionSteps, editCondition]
  );

  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const stepKey = steps[activeStep];
  const isLastStep = activeStep === steps.length - 1;
  const isConditionStep = (conditionSteps as StepKey[]).includes(stepKey);

  const stepLabels: Record<StepKey, string> = {
    level: t('calculus.createAllConditions.steps.level'),
    purchase: t('calculus.createAllConditions.steps.purchase'),
    freight: t('calculus.createAllConditions.steps.freight'),
    salesPrice: t('calculus.createAllConditions.steps.salesPrice'),
    rounding: t('calculus.createAllConditions.steps.rounding'),
    summary: t('calculus.createAllConditions.steps.summary'),
  };

  const watchedValues = watch();
  const skippedSteps = useMemo(() => {
    const set = new Set<number>();
    steps.forEach((key, index) => {
      if (
        (conditionSteps as StepKey[]).includes(key) &&
        watchedValues[skipFieldByStep[key as ConditionStepType]]
      ) {
        set.add(index);
      }
    });
    return set;
  }, [steps, conditionSteps, watchedValues]);

  const validateStep = async (key: StepKey): Promise<boolean> => {
    const schema = stepValidationSchema(t, key as ConditionStepType | 'level');
    if (!schema) return true;
    clearErrors();
    if (key === 'level' && !getValues().level) {
      setError('level', { message: t('calculus.error.chooseLevel') });
      return false;
    }
    try {
      await schema.validate(getValues(), { abortEarly: false });
      return true;
    } catch (err) {
      const validationError = err as { inner?: { path?: string; message: string }[] };
      (validationError.inner ?? []).forEach((issue) => {
        if (issue.path) {
          setError(issue.path as keyof AllConditionsFormValues, { message: issue.message });
        }
      });
      return false;
    }
  };

  // Whether the user has entered anything in a condition step — mirrors the create/skip split:
  // a step with a value is validated like "Next", an untouched one is simply skipped.
  const stepHasValue = (key: ConditionStepType): boolean => {
    const v = getValues();
    switch (key) {
      case 'purchase':
        return !!v.agreementId;
      case 'freight':
        return !!v.freightType || v.percentAddon != null || v.fixedAddon != null;
      case 'salesPrice':
        return (v.priceSourcePriority?.length ?? 0) > 0 || v.markupCoverageDegree != null;
      case 'rounding':
        return v.priceRoundingId != null;
      default:
        return false;
    }
  };

  const markStepCompleted = (index: number) =>
    setCompletedSteps((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));

  const markStepIncomplete = (index: number) =>
    setCompletedSteps((prev) => {
      if (!prev.has(index)) return prev;
      const next = new Set(prev);
      next.delete(index);
      return next;
    });

  const goNext = async () => {
    const valid = await validateStep(stepKey);
    if (!valid) return;
    if (isConditionStep) {
      setValue(skipFieldByStep[stepKey as ConditionStepType], false);
    }
    markStepCompleted(activeStep);
    setActiveStep((step) => Math.min(step + 1, steps.length - 1));
  };

  const skipStep = () => {
    if (!isConditionStep) return;
    clearErrors();
    setValue(skipFieldByStep[stepKey as ConditionStepType], true);
    markStepIncomplete(activeStep);
    setActiveStep((step) => Math.min(step + 1, steps.length - 1));
  };

  const goBack = () => {
    clearErrors();
    setActiveStep((step) => Math.max(step - 1, 0));
  };

  const goToStep = async (target: number) => {
    if (target === activeStep) return;

    // Validate the step we're leaving the same way the Next button does.
    if (stepKey === 'level') {
      // For create, the level must be chosen before any other step can be reached.
      if (!editCondition) {
        const valid = await validateStep('level');
        if (!valid) return;
      }
    } else if (isConditionStep) {
      if (stepHasValue(stepKey as ConditionStepType)) {
        const valid = await validateStep(stepKey);
        if (!valid) return;
        setValue(skipFieldByStep[stepKey as ConditionStepType], false);
        markStepCompleted(activeStep);
      } else {
        setValue(skipFieldByStep[stepKey as ConditionStepType], true);
        markStepIncomplete(activeStep);
      }
    }

    clearErrors();
    // Steps jumped over (between the current one and the target) are skipped unless completed.
    steps.forEach((key, index) => {
      if (
        index > activeStep &&
        index < target &&
        (conditionSteps as StepKey[]).includes(key) &&
        !completedSteps.has(index)
      ) {
        setValue(skipFieldByStep[key as ConditionStepType], true);
      }
    });
    setActiveStep(target);
  };

  const bindingOverride = useMemo(
    () => (editCondition ? [editCondition.getBindingLevel()] : undefined),
    [editCondition]
  );

  // The pre-filled values an edit starts from — used so only areas the user actually changed are sent.
  const editBaseline = useMemo(
    () => (editCondition ? editConditionDefaultValues(t, editCondition) : undefined),
    [editCondition, t]
  );

  const conditionsToCreate = useMemo(
    () =>
      isLastStep
        ? buildConditionsFromForm(watchedValues, supplierNumber, bindingOverride, editBaseline)
        : [],
    [isLastStep, watchedValues, supplierNumber, bindingOverride, editBaseline]
  );

  const onSave = () => {
    const conditions = buildConditionsFromForm(
      getValues(),
      supplierNumber,
      bindingOverride,
      editBaseline
    );
    if (conditions.length === 0) return;
    // Errors are surfaced via toast inside the action; keep the dialog open and swallow the rejection.
    createMultipleConditions(conditions)
      .then(onClose)
      .catch(() => undefined);
  };

  const renderStep = () => {
    switch (stepKey) {
      case 'level':
        return (
          <LevelStep
            supplierNumber={supplierNumber}
            assortmentName={selectedOrgUnitAssortment.name}
          />
        );
      case 'purchase':
        return supplierNumber ? (
          <PurchaseStep supplierNumber={supplierNumber} editCondition={editCondition} />
        ) : null;
      case 'freight':
        return supplierNumber ? (
          <FreightStep supplierNumber={supplierNumber} editCondition={editCondition} />
        ) : null;
      case 'salesPrice':
        return <SalesPriceStep supplierNumber={supplierNumber} editCondition={editCondition} />;
      case 'rounding':
        return <RoundingStep editCondition={editCondition} />;
      case 'summary':
        return (
          <SummaryStep
            conditionSteps={conditionSteps}
            supplierNumber={supplierNumber}
            assortmentName={selectedOrgUnitAssortment.name}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editCondition && editCondition
          ? t('calculus.createAllConditions.editTitle', { name: editCondition.bindingTargetName })
          : t('calculus.createAllConditions.title', {
              name: selectedSupplier ? selectedSupplier.name : selectedOrgUnitAssortment.name,
            })}
      </DialogTitle>
      <FormProvider {...formMethods}>
        <DialogContent>
          {editCondition && editCondition && (
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('calculus.createAllConditions.steps.level')}:
              </Typography>
              <LevelChip bindingType={editCondition.bindingType} active={!editCondition.inDraft} />
              <Typography variant="subtitle2">{editCondition.bindingTargetName}</Typography>
            </Stack>
          )}
          <Box sx={{ mb: 3 }}>
            <Stepper
              steps={steps.map((key) => stepLabels[key])}
              activeStep={activeStep}
              skippedSteps={skippedSteps}
              completedSteps={completedSteps}
              skippedStepIcon={<DoDisturbOnIcon />}
              onStepClick={goToStep}
            />
          </Box>

          <SuspenseWithSentryErrorBoundary>{renderStep()}</SuspenseWithSentryErrorBoundary>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <TextButton onClick={activeStep === 0 ? onClose : goBack}>
            {activeStep === 0 ? t('calculus.cancel') : t('calculus.createAllConditions.back')}
          </TextButton>

          <div>
            {isConditionStep && (
              <OutlinedButton color="secondary" size="small" onClick={skipStep} sx={{ mr: 1 }}>
                {t('calculus.createAllConditions.skip')}
              </OutlinedButton>
            )}
            {isLastStep ? (
              <ContainedButton
                color="secondary"
                size="small"
                disabled={conditionsToCreate.length === 0}
                loading={isLoadingPostChanges}
                onClick={onSave}
              >
                {t('calculus.toApproval')}
              </ContainedButton>
            ) : (
              <ContainedButton color="secondary" size="small" onClick={goNext}>
                {t('calculus.createAllConditions.next')}
              </ContainedButton>
            )}
          </div>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
